import React, { useCallback, useEffect, useState } from "react";
import NumberFormat from "react-number-format";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  IconButton,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { CalculateMeasure, CalculateMeasureGroup } from "assets/material-custom-icons";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useDealActions } from "store";

import { IGroupsFormContext, MeasureData as IMeasureData, useGroupsForm } from "./GroupsFormContext";
import { MeasureData } from "./MeasureData";

interface Props {
  investorGroup: string | null;
  groupMeasureData: IMeasureData[];
  valuePrefix?: string;
  deal_id: number;
  calculateMeasureData: IGroupsFormContext["calculateMeasureData"];
  isAllowedNegative: boolean;
  parentDealId: number | null;
  isMeasureGlobalValue: boolean;
  setSelectedGroups: (groups: string[]) => void;
  selectedGroups: string[];
  group_id: number;
}

export const GroupedMeasures: React.FC<Props> = ({
  investorGroup,
  groupMeasureData,
  valuePrefix,
  deal_id,
  calculateMeasureData,
  isAllowedNegative,
  parentDealId,
  isMeasureGlobalValue,
  setSelectedGroups,
  selectedGroups,
  group_id
}) => {
  const { getGroupedMeasureDataCalcAction } = useDealActions();
  const [expanded, setExpanded] = useState<boolean>(false);

  const toggleExpanded = () => setExpanded((prev) => !prev);

  const getMeasureGroupTotal = useCallback(() => {
    return groupMeasureData.reduce((acc, item) => acc + Number(item.value), 0);
  }, [groupMeasureData]);

  const [totalValue, setTotalValue] = useState<string>(String(getMeasureGroupTotal()));

  const [splitAnchor, setSplitAnchor] = useState<HTMLButtonElement | null>(null);
  const { updateGroupMeasureData } = useGroupsForm();

  const groupTitle = investorGroup || "Ungrouped";

  const handleSplitClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setSplitAnchor(e.currentTarget);
  };

  const handleSplitClose = () => {
    setSplitAnchor(null);
  };

  const handleSplitToEntire = async () => {
    handleSplitClose();
    const { round_id, investors } = await getGroupedMeasureDataCalcAction({
      round_id: Number(groupMeasureData[0].round_id),
      split_type: "entire_commitment",
      investor_ids: groupMeasureData.map((item) => item.investor_id).filter((id) => id !== null) as number[],
      value: String(totalValue)
    });

    updateGroupMeasureData(round_id, investors, group_id);
  };

  const handleSplitToGroup = async () => {
    handleSplitClose();
    const { round_id, investors } = await getGroupedMeasureDataCalcAction({
      round_id: Number(groupMeasureData[0].round_id),
      split_type: "group_commitment",
      value: String(totalValue),
      investor_ids: groupMeasureData
        .map((item) => item.investor_id)
        .filter((id): id is number => id !== null) as number[]
    });

    updateGroupMeasureData(round_id, investors, group_id);
  };

  useEffect(() => {
    //update total value when group measure data changes
    setTotalValue(String(getMeasureGroupTotal()));
  }, [groupMeasureData]);

  return (
    <>
      <Accordion
        expanded={expanded}
        disableGutters
        sx={{
          "&.MuiAccordion-root:before": {
            backgroundColor: "transparent"
          },
          bgcolor: "transparent",
          boxShadow: "none",
          "&.Mui-expanded": {
            margin: 0
          }
        }}
      >
        <AccordionSummary
          expandIcon={
            <IconButton size="sm" onClick={toggleExpanded}>
              <ArrowForwardIosSharpIcon sx={{ transform: expanded ? "rotate(-90deg)" : "rotate(90deg)" }} />
            </IconButton>
          }
          sx={{
            flexDirection: "row-reverse",
            padding: 0,
            minHeight: "auto",
            cursor: "default",
            "&.Mui-expanded": {
              minHeight: "auto"
            },
            ".MuiAccordionSummary-expandIconWrapper": {
              marginRight: "8px",
              transform: "none",
              "&.Mui-expanded": {
                transform: "none"
              }
            },
            ".MuiAccordionSummary-content": {
              margin: 0,
              "&.Mui-expanded": {
                margin: 0
              }
            }
          }}
        >
          <Stack direction="row" alignItems="center" gap="10px" sx={{ width: "100%" }}>
            <Box
              sx={{
                width: "195px",
                bgcolor: "#EDF1F9",
                borderRadius: "8px",
                padding: "8px 16px",
                display: "inline-flex",
                alignItems: "center",
                overflow: "hidden"
              }}
            >
              <Checkbox
                sx={{
                  padding: "4px",
                  "&:hover": {
                    backgroundColor: "transparent"
                  }
                }}
                checked={selectedGroups.includes(groupTitle)}
                onChange={() =>
                  setSelectedGroups(
                    selectedGroups.includes(groupTitle)
                      ? selectedGroups.filter((group) => group !== groupTitle)
                      : [...selectedGroups, groupTitle]
                  )
                }
              />
              <MaterialTooltip title={groupTitle}>
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "14px",
                    color: "#0A3142",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%"
                  }}
                >
                  {groupTitle}
                </Typography>
              </MaterialTooltip>
            </Box>
            <Box onClick={(e) => e.stopPropagation()} sx={{ flexGrow: 1 }}>
              <NumberFormat
                thousandSeparator
                isNumericString
                variant="outlined"
                prefix={valuePrefix}
                decimalScale={2}
                label="Value"
                placeholder="Add value"
                sx={{
                  maxWidth: "160px",
                  ".MuiInputBase-root": {
                    pr: 0,
                    overflow: "hidden"
                  },
                  ".MuiInputBase-input": {
                    padding: "9.5px 8px"
                  },
                  ".MuiButtonBase-root": {
                    opacity: splitAnchor ? 1 : 0,
                    transition: "opacity 0.2s"
                  },
                  "&:hover": {
                    ".MuiButtonBase-root": {
                      opacity: 1
                    }
                  }
                }}
                allowNegative={isAllowedNegative}
                slotProps={{
                  input: {
                    endAdornment: (
                      <MaterialTooltip isShow title="Split the value">
                        <IconButton
                          disabled={!totalValue}
                          onClick={handleSplitClick}
                          sx={{
                            "&.Mui-disabled": {
                              background: "#eaeaea",
                              color: "#c0c0c0"
                            },
                            height: "42px",
                            width: "50px",
                            bgcolor: "#0A3142",
                            borderRadius: 0,

                            ":hover": {
                              bgcolor: "#0A3142"
                            }
                          }}
                        >
                          <CalculateMeasure />
                          <ArrowDropDownIcon
                            sx={{
                              fontSize: "18px",
                              color: "#fff",
                              transform: splitAnchor ? "rotate(180deg)" : "rotate(0deg)",
                              transition: "transform 0.2s ease-in-out"
                            }}
                          />
                        </IconButton>
                      </MaterialTooltip>
                    )
                  }
                }}
                customInput={TextField}
                value={totalValue}
                onValueChange={({ value }) => setTotalValue(value)}
              />
            </Box>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0, pt: "10px", pb: "10px" }}>
          <Stack gap="10px" sx={{ paddingLeft: "10px" }}>
            {groupMeasureData.map((item) => (
              <MeasureData
                key={item.key}
                measureData={item}
                valuePrefix={valuePrefix}
                deal_id={deal_id}
                calculateMeasureData={calculateMeasureData}
                isAllowedNegative={isAllowedNegative}
                parentDealId={parentDealId}
                isMeasureGlobalValue={isMeasureGlobalValue}
              />
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Popover
        open={Boolean(splitAnchor)}
        anchorEl={splitAnchor}
        onClose={handleSplitClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
              mt: "4px"
            }
          }
        }}
      >
        <Stack sx={{ py: "8px" }}>
          <MenuItem onClick={handleSplitToEntire} sx={{ gap: "12px", py: "10px", px: "16px" }}>
            <CalculateMeasure sx={{ "& path": { fill: "#1C1B1F" } }} />
            <Typography sx={{ fontSize: "14px", color: "#0A3142" }}>Split to entire commitment</Typography>
          </MenuItem>
          <MenuItem onClick={handleSplitToGroup} sx={{ gap: "12px", py: "10px", px: "16px" }}>
            <CalculateMeasureGroup />
            <Typography sx={{ fontSize: "14px", color: "#0A3142" }}>Split to group commitment</Typography>
          </MenuItem>
        </Stack>
      </Popover>
    </>
  );
};
